import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useDemoModal } from "../context/DemoModalContext";
import {
  ChevronDown, ArrowRight, Play, Menu, X,
  CalendarDays, ShieldCheck, Clock, BarChart3,
  Truck, Users, Route, Warehouse, BookOpen, LifeBuoy, Code2,
  Building2, Briefcase, Mail, Newspaper,
} from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const container = { hidden: {}, show: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } } };
const rise = { hidden: { opacity: 0, y: 18 }, show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } } };

const slugify = (s) => s.toLowerCase().replace(/\s+/g, "-");

const navLinks = [
  {
    label: "Solutions",
    menu: [
      { icon: Truck, title: "For Carriers", desc: "Running a fleet? Start here" },
      { icon: Users, title: "For Owner-Operators", desc: "Driving solo? Start here" },
      { icon: Route, title: "For Dispatchers", desc: "Coordinating loads? Start here" },
      { icon: Warehouse, title: "For Depots", desc: "Managing a yard? Start here" },
    ],
  },
  {
    label: "Resources",
    menu: [
      { icon: BookOpen, title: "Blog", desc: "Industry trends and product news" },
      { icon: LifeBuoy, title: "Help Center", desc: "Guides and support" },
      { icon: Code2, title: "API Docs", desc: "Build on the FleetPods platform" },
    ],
  },
  {
    label: "Corporate",
    menu: [
      { icon: Building2, title: "About Us", desc: "Our mission and team" },
      { icon: Briefcase, title: "Careers", desc: "Join the FleetPods crew" },
      { icon: Mail, title: "Contact", desc: "Get in touch with us" },
      { icon: Newspaper, title: "Newsroom", desc: "Latest press and updates" },
    ],
  },
];

const features = [
  { icon: CalendarDays, title: "Auto-Book", sub: "Stops & Parking" },
  { icon: ShieldCheck, title: "Secure &", sub: "Verified Locations" },
  { icon: Clock, title: "Real-Time", sub: "Updates" },
  { icon: BarChart3, title: "Data-Driven", sub: "Fleet Insights" },
];

const carriers = ["Trimac", "Rosenau Transport", "KAG Canada", "Canadian Freightways", "Steele's Transportation"];

// Interactive building hotspots (positions = % of the hub image; s = depth scale)
const HOTSPOTS = [
  { id: "docks", x: 73, y: 17, s: 0.78, title: "Smart Dock Bays", desc: "Auto-scheduled loading, no idling, no waiting." },
  { id: "fuel", x: 24, y: 21, s: 0.86, title: "Fuel & Rest Stop", desc: "Top up and grab a break before the next leg." },
  { id: "office", x: 52, y: 40, s: 1.0, title: "Dispatch Office", desc: "Live oversight of every truck, route and booking." },
  { id: "pods", x: 23, y: 52, s: 1.12, title: "Pod Village", desc: "Private, secure rest pods booked from the app." },
  { id: "gate", x: 82, y: 75, s: 1.26, title: "Verified Hub Entry", desc: "ID-checked gate access, in and out in seconds." },
];

const Hotspot = ({ data, active, onSelect }) => {
  const [hover, setHover] = useState(false);
  const open = hover || active;
  const flip = data.x > 58;
  return (
    <div
      className="absolute z-20 -translate-x-1/2 -translate-y-full"
      style={{ left: `${data.x}%`, top: `${data.y}%` }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {/* claymorphism teardrop pin (tip points at the rooftop) */}
      <button
        data-testid={`hotspot-${data.id}`}
        onClick={() => onSelect(data.id)}
        aria-label={data.title}
        className="group relative flex origin-bottom cursor-pointer flex-col items-center transition-transform duration-200"
        style={{ transform: `scale(${data.s * (open ? 1.12 : 1)})` }}
      >
        <svg
          width="46"
          height="60"
          viewBox="0 0 44 58"
        >
          <defs>
            {/* TRUE inner shadows — clipped strictly inside the alpha, nothing bleeds past the edge */}
            <filter id={`clay-${data.id}`} x="-10%" y="-10%" width="120%" height="120%">
              {/* dark inner shadow along the bottom (offset up → band sits at bottom, inside only) */}
              <feOffset in="SourceAlpha" dx="0" dy="-4" result="do" />
              <feGaussianBlur in="do" stdDeviation="3" result="db" />
              <feComposite in="SourceAlpha" in2="db" operator="out" result="dband" />
              <feFlood floodColor="#A02800" floodOpacity="0.5" result="dcol" />
              <feComposite in="dcol" in2="dband" operator="in" result="darkInner" />

              {/* soft white highlight along the top (offset down → band sits at top, inside only) */}
              <feOffset in="SourceAlpha" dx="0" dy="3" result="lo" />
              <feGaussianBlur in="lo" stdDeviation="2" result="lb" />
              <feComposite in="SourceAlpha" in2="lb" operator="out" result="lband" />
              <feFlood floodColor="#FFFFFF" floodOpacity="0.5" result="lcol" />
              <feComposite in="lcol" in2="lband" operator="in" result="lightInner" />

              <feMerge>
                <feMergeNode in="SourceGraphic" />
                <feMergeNode in="darkInner" />
                <feMergeNode in="lightInner" />
              </feMerge>
            </filter>
          </defs>

          {/* flat FleetPods orange + see-through chevron knockout */}
          <path
            filter={`url(#clay-${data.id})`}
            fillRule="evenodd"
            fill="#FF5722"
            d="M22 2 C10 2 2 11 2 22 C2 33 12 42 22 55 C32 42 42 33 42 22 C42 11 34 2 22 2 Z M14 17 L22 24.5 L30 17 L30 22 L22 29.5 L14 22 Z"
          />
        </svg>
      </button>

      {open && (
        <div className={`absolute bottom-full mb-2 w-52 rounded-2xl border border-black/5 bg-white p-3 text-left shadow-[0_16px_30px_-10px_rgba(15,23,42,0.3)] ${flip ? "right-0" : "left-1/2 -translate-x-1/2"}`}>
          <p className="font-display text-sm font-700 text-[#0A234A]">{data.title}</p>
          <p className="mt-0.5 text-xs font-500 leading-snug text-neutral-500">{data.desc}</p>
        </div>
      )}
    </div>
  );
};

// Desktop nav item with a hover mega-dropdown for caret sections
const NavItem = ({ label, menu }) => {
  const [open, setOpen] = useState(false);
  const slug = slugify(label);

  if (!menu) {
    return (
      <a href="#" data-testid={`nav-link-${slug}`}
        className="flex items-center gap-1 text-[15px] font-500 text-neutral-700 transition-colors hover:text-neutral-900">
        {label}
      </a>
    );
  }

  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button
        type="button"
        data-testid={`nav-link-${slug}`}
        aria-expanded={open}
        className="flex items-center gap-1 text-[15px] font-500 text-neutral-700 transition-colors hover:text-neutral-900"
      >
        {label}
        <ChevronDown className={`h-4 w-4 text-neutral-400 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.18, ease: EASE }}
            className="absolute left-0 top-full z-50 pt-3"
            data-testid={`nav-dropdown-${slug}`}
          >
            <div className="w-[19rem] rounded-2xl border border-black/5 bg-white p-2 shadow-[0_20px_44px_-16px_rgba(15,23,42,0.28)]">
              {menu.map((item) => (
                <a
                  key={item.title}
                  href="#"
                  data-testid={`nav-dropdown-${slug}-${slugify(item.title)}`}
                  className="group flex items-start rounded-xl p-3 transition-colors duration-200 hover:bg-[#FF5722]/8"
                >
                  <div className="leading-tight">
                    <p className="text-sm font-700 text-[#0A234A]">{item.title}</p>
                    <p className="mt-0.5 text-xs font-500 text-neutral-500">{item.desc}</p>
                  </div>
                </a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const AUTH_ITEMS = [
  { id: "login", label: "Login", disabled: true },
  { id: "demo", label: "Request a Demo", disabled: false },
];

// Apple-style segmented slider: a claymorphism pill springs under whichever
// segment is hovered (resting on "Book a Demo"). Login is intentionally disabled.
const AuthSlider = ({ full = false, testid = "auth-slider" }) => {
  const [hovered, setHovered] = useState("demo");
  const { openDemo } = useDemoModal();
  return (
    <div
      data-testid={testid}
      onMouseLeave={() => setHovered("demo")}
      className={`relative inline-flex items-center rounded-full bg-white p-1 ring-1 ring-[#FF5722] ${full ? "w-full" : ""}`}
    >
      {AUTH_ITEMS.map((it) => {
        const active = hovered === it.id;
        return (
          <button
            key={it.id}
            type="button"
            data-testid={`${testid}-${it.id}`}
            aria-disabled={it.disabled}
            onMouseEnter={() => setHovered(it.id)}
            onFocus={() => setHovered(it.id)}
            onClick={(e) => { e.preventDefault(); if (!it.disabled) openDemo(); }}
            className={`relative z-10 rounded-full px-5 py-2 text-[15px] font-600 transition-colors duration-300 ${full ? "flex-1 text-center" : ""} ${
              active ? "text-white" : "text-neutral-700"
            } ${it.disabled ? "cursor-not-allowed" : "cursor-pointer"}`}
          >
            {active && (
              <motion.span
                layoutId={`${testid}-pill`}
                transition={{ type: "spring", stiffness: 380, damping: 30 }}
                className="absolute inset-0 -z-10 rounded-full bg-[#FF5722] shadow-[0_10px_20px_-6px_rgba(255,87,34,0.55),inset_0_2px_2px_rgba(255,255,255,0.4),inset_0_-4px_6px_rgba(160,40,0,0.35)]"
              />
            )}
            <span className="relative">{it.label}</span>
          </button>
        );
      })}
    </div>
  );
};

const Logo = () => (
  <a href="#" data-testid="nav-logo" className="flex items-center gap-2.5">
    <img src="/logo192.png" alt="" className="h-9 w-9" draggable="false" />
    <img src="/fleetpods_logo.png" alt="Fleetpods" className="h-7 w-auto" draggable="false" />
  </a>
);

export const Hero = () => {
  const { openDemo } = useDemoModal();
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileSub, setMobileSub] = useState(null);
  const [activePin, setActivePin] = useState(null);

  return (
    <div data-testid="hero-section" className="relative flex min-h-screen w-full flex-col overflow-hidden bg-white">
      {/* NAVBAR */}
      <header className="fixed inset-x-0 top-0 z-50 bg-white/90 shadow-[0_6px_16px_rgba(15,23,42,0.10),0_16px_40px_-12px_rgba(15,23,42,0.22)] backdrop-blur-xl">
        <nav className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-5 lg:px-10">
          <Logo />
          <div className="hidden items-center gap-8 lg:flex">
            {navLinks.map((l) => (
              <NavItem key={l.label} label={l.label} menu={l.menu} />
            ))}
          </div>
          <div className="hidden lg:block">
            <AuthSlider testid="nav-auth-slider" />
          </div>
          <button data-testid="nav-menu-toggle" onClick={() => setMenuOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-lg text-neutral-800 hover:bg-black/5 lg:hidden">
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </nav>
        {menuOpen && (
          <div className="flex flex-col gap-1 border-t border-black/5 bg-white px-6 py-4 lg:hidden">
            {navLinks.map((l) => {
              const slug = slugify(l.label);
              if (!l.menu) {
                return (
                  <a key={l.label} href="#" data-testid={`mobile-nav-link-${slug}`}
                    className="rounded-lg px-3 py-3 text-[15px] font-500 text-neutral-700 hover:bg-black/5">{l.label}</a>
                );
              }
              const isOpen = mobileSub === l.label;
              return (
                <div key={l.label}>
                  <button
                    type="button"
                    data-testid={`mobile-nav-link-${slug}`}
                    onClick={() => setMobileSub(isOpen ? null : l.label)}
                    className="flex w-full items-center justify-between rounded-lg px-3 py-3 text-[15px] font-500 text-neutral-700 hover:bg-black/5"
                  >
                    {l.label}
                    <ChevronDown className={`h-4 w-4 text-neutral-400 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.22, ease: EASE }}
                        className="overflow-hidden"
                      >
                        <div className="flex flex-col gap-0.5 pb-2 pl-2">
                          {l.menu.map((item) => (
                            <a
                              key={item.title}
                              href="#"
                              data-testid={`mobile-nav-dropdown-${slug}-${slugify(item.title)}`}
                              className="flex items-center rounded-lg px-3 py-2.5 hover:bg-[#FF5722]/8"
                            >
                              <span className="text-sm font-600 text-[#0A234A]">{item.title}</span>
                            </a>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
            <div className="mt-2">
              <AuthSlider full testid="mobile-auth-slider" />
            </div>
          </div>
        )}
      </header>

      {/* MAIN */}
      <main className="mx-auto flex w-full max-w-[1400px] flex-1 items-center px-6 pt-28 lg:px-10 lg:pt-24">
        <div className="grid w-full items-center gap-10 lg:grid-cols-2 lg:gap-8">
          {/* TEXT */}
          <motion.div variants={container} initial="hidden" animate="show" className="text-center lg:col-start-1 lg:row-start-1 lg:text-left">
            <motion.p variants={rise} data-testid="hero-overline"
              className="mb-4 text-sm font-700 uppercase tracking-[0.15em] text-[#FF5722]">
              Smart Parking. Better Rest.
            </motion.p>

            <motion.h1 variants={rise} data-testid="hero-headline"
              className="font-display text-3xl font-700 leading-[1.05] tracking-tight text-[#0A234A] sm:text-6xl">
              Smarter logistics,
              <span className="block text-[#FF5722]">smoother every mile.</span>
            </motion.h1>

            <motion.p variants={rise} data-testid="hero-subcopy"
              className="mx-auto mt-6 max-w-md text-lg font-400 leading-relaxed text-neutral-500 lg:mx-0">
              FleetPods helps transport logistics teams eliminate delivery uncertainty by
              auto-booking secure, verified rest pods along every route, so drivers arrive
              safe, rested, compliant, and on time.
            </motion.p>

            <motion.div variants={rise} className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:items-center">
              <a href="#" data-testid="hero-primary-cta"
                onClick={(e) => { e.preventDefault(); openDemo(); }}
                className="group flex items-center justify-center gap-2.5 rounded-2xl bg-[#FF5722] px-7 py-3.5 text-base font-600 text-white shadow-[0_10px_20px_-6px_rgba(255,87,34,0.55),inset_0_2px_2px_rgba(255,255,255,0.4),inset_0_-4px_6px_rgba(160,40,0,0.35)] transition-all duration-300 hover:bg-[#F4511E] hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98]">
                Request a Demo
                <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
              <a href="#how-it-works" data-testid="hero-secondary-cta"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className="flex items-center justify-center gap-2.5 rounded-2xl bg-white px-7 py-3.5 text-base font-600 text-neutral-800 shadow-[0_8px_18px_-8px_rgba(15,23,42,0.22),inset_0_2px_2px_rgba(255,255,255,0.95),inset_0_-4px_6px_rgba(15,23,42,0.07),0_0_0_1px_rgba(15,23,42,0.06)] transition-all duration-300 hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98]">
                <Play className="h-4 w-4 fill-neutral-800" /> See How It Works
              </a>
            </motion.div>
          </motion.div>

          {/* HUB RENDER — sits between text and features on mobile, right column on desktop */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.25, duration: 1, ease: EASE }}
            className="lg:col-start-2 lg:row-start-1 lg:row-span-2 lg:self-center lg:-mr-24 xl:-mr-40"
          >
            <div className="relative lg:scale-[1.22] lg:origin-center">
              <img
                data-testid="hero-image"
                src="/hero_hub.png"
                alt="FleetPods logistics hub with secure truck parking and dock bays"
                className="h-auto w-full select-none lg:[mask-image:linear-gradient(to_right,black_88%,transparent_100%)] lg:[-webkit-mask-image:linear-gradient(to_right,black_88%,transparent_100%)]"
                draggable="false"
              />
              {HOTSPOTS.map((h) => (
                <Hotspot
                  key={h.id}
                  data={h}
                  active={activePin === h.id}
                  onSelect={(id) => setActivePin((p) => (p === id ? null : id))}
                />
              ))}
            </div>
          </motion.div>

          {/* FEATURE ROW */}
          <motion.div
            variants={rise}
            initial="hidden"
            animate="show"
            data-testid="hero-features"
            className="grid grid-cols-2 gap-x-6 gap-y-5 sm:grid-cols-4 lg:col-start-1 lg:row-start-2 lg:mt-2"
          >
            {features.map((f) => (
              <div key={f.title} className="flex items-center gap-3">
                <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-neutral-100 ring-1 ring-black/5">
                  <f.icon className="h-5 w-5 text-neutral-700" />
                </span>
                <div className="leading-tight">
                  <p className="whitespace-nowrap text-sm font-700 text-neutral-900">{f.title}</p>
                  <p className="whitespace-nowrap text-xs font-500 text-neutral-500">{f.sub}</p>
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </main>

      {/* TRUST BAR */}
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }}
        transition={{ delay: 0.8, duration: 0.7 }}
        className="mx-auto w-full max-w-[1400px] px-6 pb-8 pt-4 lg:px-10"
        data-testid="hero-trust"
      >
        <div className="border-t border-black/8 pt-6">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:gap-10">
            {/* association */}
            <div className="flex items-center gap-3.5">
              <span className="text-[11px] font-700 uppercase tracking-[0.2em] text-neutral-400">Proud Support From</span>
              <img src="/amta-logo.png" alt="Alberta Motor Transport Association" className="h-8 w-auto" draggable="false" />
            </div>

            <span className="hidden h-8 w-px bg-black/10 lg:block" />

            {/* carriers */}
            <div className="flex flex-col gap-2">
              <span className="text-[11px] font-700 uppercase tracking-[0.2em] text-neutral-400">Serving Alberta's leading carriers</span>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                {carriers.map((name, i) => (
                  <span key={name} className="flex items-center gap-4">
                    {i > 0 && <span className="h-1 w-1 rounded-full bg-neutral-300" />}
                    <span className="font-display text-sm font-600 whitespace-nowrap text-neutral-500 transition-colors hover:text-neutral-900">{name}</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};
